import React, { useEffect, useRef } from "react";
import { gsap } from "../../lib/gsap";
import { FaAward, FaCertificate, FaGraduationCap } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";
import profile from "../../data/profile";

export default function EducationSection({ subSlide = 0 }) {
  const sectionRef = useRef(null);
  const slidesRef = useRef([]);

  // Cross-fade between the two sub-slides
  useEffect(() => {
    const slides = slidesRef.current.filter(Boolean);
    slides.forEach((s, i) => {
      if (!s) return;
      const isActive = i === subSlide;
      gsap.to(s, {
        opacity: isActive ? 1 : 0,
        y: isActive ? 0 : (i < subSlide ? -30 : 30),
        pointerEvents: isActive ? "auto" : "none",
        duration: 0.55,
        ease: "power3.out",
      });
    });
  }, [subSlide]);

  useEffect(() => {
    // Ensure initial slide 0 is visible
    const slides = slidesRef.current.filter(Boolean);
    slides.forEach((s, i) => {
      if (!s) return;
      s.style.opacity = i === 0 ? "1" : "0";
      s.style.pointerEvents = i === 0 ? "auto" : "none";
    });
  }, []);

  const slideLabels = ["Degrees", "Certifications & Awards"];

  return (
    <section ref={sectionRef} className="snap-section" data-testid="education-section"
      style={{ background: "var(--paper)", color: "var(--ink)" }}>
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(45deg, var(--ink) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

      <div className="relative h-full w-full px-6 md:px-16 pt-16 md:pt-24 pb-4 md:pb-10 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-end mb-2 md:mb-6">
          <div>
            <p className="font-mono text-[9px] md:text-[10px] tracking-[0.35em] uppercase text-[var(--accent-2)]">Education</p>
            <h2 className="mt-1 md:mt-2 font-serif text-[clamp(1.5rem,7vw,4.5rem)] leading-[0.9] tracking-tight">
              What I <span className="italic text-[var(--accent-2)]">studied.</span>
            </h2>
          </div>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--muted)] hidden md:block">
            0{subSlide + 1} / 02 · {slideLabels[subSlide]}
          </span>
        </div>

        {/* Sub-slide progress bar */}
        <div className="flex items-center gap-3 mb-2 md:mb-6">
          {[0, 1].map(i => (
            <div key={i} className="flex-1 h-[2px] bg-[var(--line)] overflow-hidden rounded-full">
              <div className="h-full bg-[var(--accent-2)] transition-all duration-500"
                   style={{ width: i <= subSlide ? "100%" : "0%" }} />
            </div>
          ))}
          <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--muted)] whitespace-nowrap">
            {subSlide === 1 ? "Scroll for Contact" : "Scroll for certs & awards"}
          </span>
        </div>

        {/* Slides container */}
        <div className="relative flex-1">
          {/* Slide 1: Degrees */}
          <div ref={el => (slidesRef.current[0] = el)} className="absolute inset-0 grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-6">
            {profile.education.map((e, i) => (
              <div key={e.id} data-testid={`education-card-${i}`}
                className="rounded-2xl p-3.5 md:p-7 border border-[var(--line-strong)] bg-white/70 backdrop-blur flex flex-col">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-7 h-7 md:w-10 md:h-10 rounded-full border border-[var(--line-strong)] flex items-center justify-center text-[var(--accent-2)]">
                    <FaGraduationCap size={13} />
                  </div>
                  <div className="flex-1">
                    <p className="font-mono text-[9px] md:text-[10px] tracking-[0.22em] uppercase text-[var(--muted)]">
                      {e.period} to {e.periodEnd}
                    </p>
                  </div>
                  <span className="font-mono text-[9px] md:text-[10px] tracking-[0.2em] uppercase text-[var(--accent-2)]">0{i + 1}</span>
                </div>
                <h3 className="mt-1.5 md:mt-4 font-serif text-[15px] md:text-2xl leading-tight">{e.degree}</h3>
                <p className="mt-0.5 md:mt-1 text-[12px] md:text-sm text-[var(--accent-2)] italic">{e.institution}</p>
                <p className="mt-1 md:mt-2 text-[10px] md:text-xs font-mono tracking-[0.15em] uppercase text-[var(--muted)]">{e.grade}</p>
                <div className="mt-1.5 md:mt-4 flex flex-wrap gap-1 md:gap-1.5">
                  {e.modules.slice(0, 3).map(m => (
                    <span key={m} className="px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[9px] md:text-[10px] font-mono uppercase tracking-[0.12em] border border-[var(--line)] text-[var(--muted)] md:hidden">
                      {m}
                    </span>
                  ))}
                  {e.modules.slice(0, 6).map(m => (
                    <span key={m} className="hidden md:inline-block px-2 py-1 rounded-full text-[10px] font-mono uppercase tracking-[0.12em] border border-[var(--line)] text-[var(--muted)]">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Slide 2: Certifications + Awards */}
          <div ref={el => (slidesRef.current[1] = el)} className="absolute inset-0 grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-6"
               data-testid="education-slide-1">
            <div className="rounded-2xl p-3.5 md:p-7 border border-[var(--line-strong)] bg-white/70 backdrop-blur">
              <div className="flex items-center gap-2 mb-1.5 md:mb-4">
                <FaCertificate size={12} className="text-[var(--accent-2)]" />
                <p className="font-mono text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-[var(--muted)]">Certifications</p>
              </div>
              <ul className="space-y-1 md:space-y-3">
                {profile.certifications.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 md:gap-3 border-b border-[var(--line)] last:border-b-0 pb-1 md:pb-3 last:pb-0">
                    <span className="font-mono text-[9px] md:text-[10px] text-[var(--accent-2)] mt-0.5 md:mt-1">0{i + 1}</span>
                    <div className="flex-1">
                      {c.link ? (
                        <a href={c.link} target="_blank" rel="noopener noreferrer"
                           data-testid={`cert-link-${i}`}
                           className="text-[12px] md:text-[14px] leading-tight font-medium inline-flex items-center gap-1.5 hover:text-[var(--accent-2)] transition-colors underline decoration-[var(--line-strong)] underline-offset-2">
                          {c.name}
                          <FiExternalLink size={10} className="shrink-0" />
                        </a>
                      ) : (
                        <p className="text-[12px] md:text-[14px] leading-tight font-medium">{c.name}</p>
                      )}
                      <p className="text-[10px] md:text-[11px] text-[var(--muted)] mt-0.5 md:mt-1">{c.org} · {c.year}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl p-3 md:p-7 border border-[var(--line-strong)] bg-[var(--ink)] text-[var(--paper)]">
              <div className="flex items-center gap-2 mb-1 md:mb-4">
                <FaAward size={13} className="text-[var(--accent)]" />
                <p className="font-mono text-[9px] md:text-[10px] tracking-[0.3em] uppercase text-[color:rgba(245,242,236,0.6)]">Awards & Honours</p>
              </div>
              <ul className="space-y-0.5 md:space-y-4">
                {profile.achievements.map((a, i) => (
                  <li key={i} className="text-[11px] md:text-sm leading-snug border-b border-[rgba(245,242,236,0.1)] last:border-b-0 pb-0.5 md:pb-3 last:pb-0">
                    <div className="flex items-start gap-1.5 md:gap-2">
                      <span className="text-[var(--accent)] mt-0.5">★</span>
                      <div>
                        <p className="font-medium">{a.name}</p>
                        <p className="text-[9px] md:text-[11px] text-[color:rgba(245,242,236,0.55)] font-mono uppercase tracking-[0.1em] mt-0.5">{a.org} · {a.year}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
